import type { ReactNode } from 'react';
import { useFormikContext } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { OffEmailsDir } from 'modules/general';
import SolarIcon from 'components/icons/SolarIcon';
import spinnerTitle from 'components/others/spinnerTitle';
import { EMAIL_BODY_MAX_LENGTH, useEmailRecipientsPicker, type SendDocumentEmailValues } from './model';

interface SendDocumentEmailFormProps {
    onClose: () => void;
    children: ReactNode;
}

const SendDocumentEmailForm = ({ onClose, children }: SendDocumentEmailFormProps) => {
    const { t } = useTranslation(['Proposal', 'common', 'EmailsDir']);
    const { values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting } = useFormikContext<SendDocumentEmailValues>();

    const { showPicker, openPicker, closePicker, selectEmails } = useEmailRecipientsPicker();

    return (
        <>
            <Form noValidate onSubmit={handleSubmit} className="off-coming-form d-flex flex-column h-100">
                <div className="off-coming-content px-3">
                    <Form.Group className="mb-3" controlId="object-document-email">
                        <Form.Label>{t('edit.generate.sendDialog.emailLabel')}</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Control
                                name="email"
                                type="text"
                                value={values.email}
                                onChange={handleChange}
                                onFocus={(event) => event.target.select()}
                                onBlur={handleBlur}
                                disabled={isSubmitting}
                                isInvalid={Boolean(touched.email && errors.email)}
                                placeholder={t('edit.generate.sendDialog.emailPlaceholder')}
                                autoComplete="email"
                                autoFocus
                            />
                            <Button type="button" size="sm" variant="light-secondary" className="icon-btn-24 flex-shrink-0" onClick={openPicker} disabled={isSubmitting} title={t('EmailsDir:openPicker')} aria-label={t('EmailsDir:openPicker')}>
                                <SolarIcon name="card-list" />
                            </Button>
                        </div>
                        <Form.Control.Feedback type="invalid" className={touched.email && errors.email ? 'd-block' : undefined}>{errors.email}</Form.Control.Feedback>
                        <div className="form-text">{t('edit.generate.sendDialog.emailHint')}</div>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="object-document-email-body">
                        <Form.Label>{t('edit.generate.sendDialog.bodyLabel')}</Form.Label>
                        <Form.Control
                            name="emailBody"
                            as="textarea"
                            rows={5}
                            value={values.emailBody}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            maxLength={EMAIL_BODY_MAX_LENGTH}
                            placeholder={t('edit.generate.sendDialog.bodyPlaceholder')}
                            style={{ resize: 'vertical' }}
                        />
                        <div className="form-text text-end">{values.emailBody.length} / {EMAIL_BODY_MAX_LENGTH}</div>
                    </Form.Group>
                    {children}
                </div>
                <div className="p-3 offcanvas-footer off-coming-footer">
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                            {t('common:buttons.cancel')}
                        </Button>
                        <Button type="submit" variant="info" className="min100px" disabled={isSubmitting}>
                            {spinnerTitle(t('edit.generate.sendDialog.submit'), isSubmitting, t('edit.generate.sendDialog.submitting'))}
                        </Button>
                    </div>
                </div>
            </Form>
            <OffEmailsDir show={showPicker} onHide={closePicker} onSelect={selectEmails} />
        </>
    );
};

export default SendDocumentEmailForm;
